import {Component, EventEmitter, Input, OnDestroy, OnInit, Output, SimpleChanges, OnChanges} from '@angular/core';
import {LocationDto} from '../../../model/continent';
import {TitleBackgroundComponent} from '../title-background/title-background.component';
import {Location, NgForOf, NgIf, NgStyle} from '@angular/common';
import {scrollUp} from '../../../functions/scroll-up';
import {Router} from '@angular/router';
import {Services} from '../../../model/services';
import {AboutElComponent} from '../../pages/services-page/elements/about-el/about-el.component';

@Component({
  selector: 'app-background-looped-slider',
  imports: [
    TitleBackgroundComponent,
    NgStyle,
    NgIf,
    NgForOf,
    AboutElComponent,
    AboutElComponent,
  ],
  templateUrl: './background-looped-slider.component.html',
  styleUrl: './background-looped-slider.component.scss'
})
export class BackgroundLoopedSliderComponent implements OnInit, OnDestroy, OnChanges {
  @Input() locations: LocationDto[] = [];
  @Input() services: Services[] = [];
  @Input() autoplay: boolean = false;
  @Input() slideInterval: number = 0;
  @Input() showButton: boolean = true;
  @Input() currentSlideIndex: number = 0;
  @Input() slideType: 'locations' | 'services' = 'locations';
  @Output() emitActive: EventEmitter<number> = new EventEmitter();

  constructor(
    private router: Router,
    public location: Location
  ) {}

  currentLocation: LocationDto = new LocationDto();
  currentService: Services = new Services();

  delay: number = 700;
  currentIndex = 0;
  isMobile: boolean = false;

  private isPaused: boolean = false;
  public countdown: number = this.slideInterval;
  private countdownIntervalId: any = null;

  private lastExternalIndex: number = -1;
  private initialized = false;

  get allItems(): any[] {
    if (this.slideType === 'locations') {
      return this.locations;
    } else {
      return this.services;
    }
  }

  ngOnInit() {
    this.currentIndex = this.currentSlideIndex;
    this.lastExternalIndex = this.currentIndex;
    this.initialized = true;
    if (this.autoplay && this.hasMultipleItems) {
      this.startAutoSlide(this.slideInterval);
    }
    if (window.innerWidth <= 769) {
      this.isMobile = true;
    }

    this.emitActive.emit(this.currentIndex);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!this.initialized) {
      return;
    }
    if (changes['currentSlideIndex'] && !changes['currentSlideIndex'].firstChange) {
      const newIndex = changes['currentSlideIndex'].currentValue;
      this.lastExternalIndex = newIndex;
      this.slideToIndex(newIndex);
    }
  }

  slideToIndex(targetIndex: number): void {
    const allItems = this.allItems;
    if (!this.hasMultipleItems || targetIndex < 0 || targetIndex >= allItems.length) {
      return;
    }
    if (targetIndex === this.currentIndex) {
      this.emitActive.emit(this.currentIndex);
      return;
    }
    this.pause();
    const distance = this.getShortestDistance(this.currentIndex, targetIndex, allItems.length);
    if (distance > 0) {
      this.slideDirection = 'left';
      this.translateX = -window.innerWidth;
      this.transition = 'transform 0.7s ease';
    } else {
      this.slideDirection = 'right';
      this.translateX = window.innerWidth;
      this.transition = 'transform 0.7s ease';
    }
    setTimeout(() => {
      this.currentIndex = targetIndex;
      this.emitActive.emit(this.currentIndex);
      this.lastExternalIndex = this.currentIndex;
      this.translateX = 0;
      this.transition = '';
      setTimeout(() => {
        if (this.autoplay) {
          this.resume();
        }
      }, 1000);
    }, this.delay);
  }

  private getShortestDistance(current: number, target: number, length: number): number {
    const forwardDistance = (target - current + length) % length;
    const backwardDistance = (current - target + length) % length;
    return forwardDistance <= backwardDistance ? forwardDistance : -backwardDistance;
  }

  ngOnDestroy() {
    this.clearTimers();
  }

  get hasMultipleItems(): boolean {
    const allItems = this.allItems;
    return allItems && allItems.length > 1;
  }

  clickOn() {
    const allItems = this.allItems;
    if (this.slideType === 'locations') {
      const currentLocation = allItems[this.currentIndex] as LocationDto;
      const currentUrl = this.router.url;
      if (currentUrl === '/destinations/antarctica') {
        scrollUp();
        this.router.navigate(['/destinations/antarctica/location/route/' + currentLocation.url]).then(r => r);
      } else {
        scrollUp();
        this.router.navigate([currentUrl + '/' + currentLocation.url]).then(r => r);
      }
    } else if (this.slideType === 'services') {
    }
  }

  startAutoSlide(startTime: number) {
    if (!this.hasMultipleItems) return;
    this.clearTimers();
    this.countdown = startTime;
    this.countdownIntervalId = setInterval(() => {
      if (!this.isPaused && this.countdown > 0 && this.hasMultipleItems) {
        this.countdown -= 0.1;
        if (this.countdown <= 0) {
          this.nextSlide();
          this.startAutoSlide(this.slideInterval);
        }
      }
    }, 100);
  }

  pause() {
    if (this.countdownIntervalId) {
      clearInterval(this.countdownIntervalId);
      this.countdownIntervalId = null;
    }
    this.isPaused = true;
  }

  resume() {
    if (this.isPaused && this.hasMultipleItems) {
      this.isPaused = false;
      this.startAutoSlide(this.countdown);
    }
  }

  stop() {
    this.clearTimers();
    this.isPaused = false;
  }

  clearTimers() {
    if (this.countdownIntervalId) {
      clearInterval(this.countdownIntervalId);
      this.countdownIntervalId = null;
    }
  }

  getPercent() {
    const allItems = this.allItems;
    return allItems.length > 1 ? 100 - ((this.countdown / this.slideInterval) * 100) : 0;
  }

  getCurrentItem(idx: number) {
    const allItems = this.allItems;
    if (this.slideType === 'locations') {
      return allItems[idx] ?? new LocationDto();
    } else {
      return allItems[idx] ?? new Services();
    }
  }

  get prevIndex() {
    const allItems = this.allItems;
    return (this.currentIndex - 1 + allItems.length) % allItems.length;
  }

  get nextIndex() {
    const allItems = this.allItems;
    return (this.currentIndex + 1) % allItems.length;
  }

  get hasPrev() {
    const allItems = this.allItems;
    return allItems.length > 1;
  }

  get hasNext() {
    const allItems = this.allItems;
    return allItems.length > 1;
  }

  getContainerWidth() {
    return window.innerWidth;
  }

  isDragging = false;
  dragged = false;
  startX = 0;
  startY = 0;
  translateX = 0;
  transition = '';
  slideDirection = '';

  onMouseDown(event?: MouseEvent) {
    if (!this.isMobile) {
      return;
    }
    if (!this.hasMultipleItems) return;
    this.pause();
    this.isDragging = true;
    this.startX = event?.clientX ?? 0;
    this.startY = event?.clientY ?? 0;
    this.transition = '';
    document.body.style.cursor = 'grabbing';
  }

  onTouchStart(event?: TouchEvent) {
    if (!this.isMobile) {
      return;
    }
    if (!this.hasMultipleItems) return;
    this.pause();
    this.isDragging = true;
    this.startX = event?.touches[0].clientX ?? 0;
    this.startY = event?.touches[0].clientY ?? 0;
    this.transition = '';
    document.body.style.cursor = 'grabbing';
  }

  onMouseMove(event: MouseEvent) {
    if (!this.isMobile) {
      return;
    }
    if (!this.hasMultipleItems) return;
    if (!this.isDragging) return;
    this.dragged = true;
    const deltaX = event.clientX - this.startX;
    const deltaY = event.clientY - this.startY;
    if (Math.abs(deltaX) > Math.abs(deltaY)) {
      event.preventDefault();
      this.translateX = deltaX;
      if (deltaX > 0) {
        this.slideDirection = 'right';
      } else if (deltaX < 0) {
        this.slideDirection = 'left';
      }
    }
  }

  onTouchMove(event: TouchEvent) {
    if (!this.isMobile) {
      return;
    }
    if (!this.hasMultipleItems) return;
    if (!this.isDragging) return;
    const deltaX = event.touches[0].clientX - this.startX;
    const deltaY = event.touches[0].clientY - this.startY;
    if (Math.abs(deltaX) > Math.abs(deltaY)) {
      event.preventDefault();
      this.dragged = true;
      this.translateX = deltaX;
      if (deltaX > 0) {
        this.slideDirection = 'right';
      } else if (deltaX < 0) {
        this.slideDirection = 'left';
      }
    }
  }

  onSlidingEnd() {
    if (!this.hasMultipleItems) return;
    if (!this.isDragging) return;
    this.isDragging = false;
    const threshold = window.innerWidth / 5;
    this.transition = 'transform 0.7s ease';
    if (this.slideDirection === 'right' && this.translateX > threshold && this.hasPrev) {
      this.moveRight();
    } else if (this.slideDirection === 'left' && this.translateX < -threshold && this.hasNext) {
      this.moveLeft();
    } else {
      this.translateX = 0;
    }
    this.dragged = false;
    document.body.style.cursor = 'default';
    setTimeout(() => {
      if (this.autoplay) {
        this.resume();
      }
    }, 1000);
  }

  moveLeft() {
    if (!this.hasMultipleItems) return;
    this.pause();
    this.slideDirection = 'left';
    this.translateX = -window.innerWidth;
    this.transition = 'transform 0.7s ease';
    setTimeout(() => {
      this.currentIndex = this.nextIndex;
      this.emitActive.emit(this.currentIndex);
      this.lastExternalIndex = this.currentIndex;
      this.translateX = 0;
      this.transition = '';
      setTimeout(() => {
        if (this.autoplay) {
          this.resume();
        }
      }, 1000);
    }, this.delay);
  }

  moveRight() {
    if (!this.hasMultipleItems) return;
    this.pause();
    this.slideDirection = 'right';
    this.translateX = window.innerWidth;
    this.transition = 'transform 0.7s ease';
    setTimeout(() => {
      this.currentIndex = this.prevIndex;
      this.emitActive.emit(this.currentIndex);
      this.lastExternalIndex = this.currentIndex;
      this.translateX = 0;
      this.transition = '';
      setTimeout(() => {
        if (this.autoplay) {
          this.resume();
        }
      }, 1000);
    }, this.delay);
  }

  nextSlide() {
    if (!this.hasMultipleItems) return;
    this.moveLeft();
  }

  onMouseLeave() {
    if (!this.isMobile) {
      return;
    }
    if (this.isDragging && this.hasMultipleItems) {
      this.onSlidingEnd();
      this.dragged = false;
    }
  }
}
